import React, { useCallback, useEffect, useMemo } from 'react'
import { Table } from 'antd'
import { isNil, some, toString } from 'lodash'

import { showErrorNotification } from '../../../core/helpers'

import { PAGINATION } from '../../../common/constants'

import { useLoggedInUser } from '../../../account/hooks'
import {
  useBrandCurationTasksListFilters,
  useBrandCurationTasksQuery,
} from '../../hooks'
import { createBrandCurationTaskTableColumns } from '../../services'

import BrandCurationTaskEditableRow from './brand-curation-task-editable-row'

const BrandCurationTasksTable = () => {
  const { settings: userSettings } = useLoggedInUser()

  const {
    currentPage = PAGINATION.DEFAULT_PAGE,
    pageSize = userSettings?.pageSize || PAGINATION.DEFAULT_PAGE_SIZE,
    isCurationDone,
    isQaDone,
    isQcDone,
    taskStatus,
    autoeatStatus,
    searchText,
    assignedCurator,
    assignedQa,
    assignedQc,
    duplicate,
    setFilters,
  } = useBrandCurationTasksListFilters()

  const { filters, pagination } = useMemo(
    () => ({
      filters: {
        taskStatus,
        autoeatStatus,
        isCurationDone,
        isQaDone,
        isQcDone,
        searchText: !isNil(searchText) ? toString(searchText) : null,
        assignedCurator: !isNil(assignedCurator)
          ? toString(assignedCurator)
          : null,
        assignedQa: !isNil(assignedQa) ? toString(assignedQa) : null,
        assignedQc: !isNil(assignedQc) ? toString(assignedQc) : null,
        duplicate,
      },
      pagination: {
        pageSize,
        currentPage,
      },
    }),
    [
      assignedCurator,
      assignedQa,
      assignedQc,
      autoeatStatus,
      currentPage,
      isCurationDone,
      isQaDone,
      isQcDone,
      pageSize,
      searchText,
      taskStatus,
      duplicate,
    ],
  )

  const { data, isLoading, error } = useBrandCurationTasksQuery(
    filters,
    pagination,
  )
  const { result, total } = data || {}

  const isDeleteButtonVisible = useMemo(
    () => some(result, task => task.brand?.isFake),
    [result],
  )

  const tableColumns = useMemo(
    () => createBrandCurationTaskTableColumns(isDeleteButtonVisible),
    [isDeleteButtonVisible],
  )

  const components = useMemo(
    () => ({
      body: {
        row: BrandCurationTaskEditableRow,
      },
    }),
    [],
  )

  const handleChangePagination = useCallback(
    (page, size) =>
      setFilters({
        currentPage: size !== pageSize ? PAGINATION.DEFAULT_PAGE : page,
        pageSize: size,
      }),
    [pageSize, setFilters],
  )

  const paginationConfig = useMemo(() => {
    if (isNil(total)) {
      return false
    }

    return {
      current: Number(currentPage),
      pageSize,
      total,
      onChange: handleChangePagination,
      onShowSizeChange: handleChangePagination,
      pageSizeOptions: PAGINATION.DEFAULT_PAGINATION_SIZE_OPTIONS,
    }
  }, [total, currentPage, pageSize, handleChangePagination])

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'Fetch brand curation tasks failed',
        description: error,
      })
    }
  }, [error])

  return (
    <Table
      loading={isLoading}
      components={components}
      rowKey="id"
      columns={tableColumns}
      dataSource={result}
      pagination={paginationConfig}
    />
  )
}

export default BrandCurationTasksTable
